import { Quote } from '../types/quote';

/**
 * Deterministic hash function for date strings
 * Same input (date) always produces the same output (hash)
 *
 * Algorithm: Simple sum of character codes + date-based multipliers
 * This ensures different dates produce different distributions
 *
 * @param dateString - ISO date string (YYYY-MM-DD)
 * @returns Positive integer hash
 */
function hashDateString(dateString: string): number {
  let hash = 0;

  // Use each character's code and position to create variation
  for (let i = 0; i < dateString.length; i++) {
    const char = dateString.charCodeAt(i);
    // Multiply by position to ensure order matters
    // Add prime number multipliers for better distribution
    hash = hash * 31 + char * (i + 1);
  }

  // Ensure positive number
  return Math.abs(hash);
}

/**
 * Advanced hash function with better distribution
 * Uses a more sophisticated algorithm for better randomness
 * while maintaining determinism
 *
 * @param dateString - ISO date string (YYYY-MM-DD)
 * @returns Positive integer hash
 */
function advancedHash(dateString: string): number {
  let hash = 5381; // Initial prime seed

  for (let i = 0; i < dateString.length; i++) {
    const char = dateString.charCodeAt(i);
    // DJB2 hash algorithm: hash * 33 + char
    hash = ((hash << 5) + hash) + char;
  }

  return Math.abs(hash);
}

/**
 * Select a daily quote based on date
 *
 * Core algorithm:
 * 1. Hash the date string to get a deterministic number
 * 2. Use modulo to map that number to a quote index
 * 3. Same date = same hash = same index = same quote
 *
 * @param quotes - Array of all available quotes
 * @param dateString - ISO date string (YYYY-MM-DD)
 * @param useAdvancedHash - Use advanced hash for better distribution (default: true)
 * @returns Selected quote, or null if no quotes available
 */
export function selectDailyQuote(
  quotes: Quote[],
  dateString: string,
  useAdvancedHash: boolean = true
): Quote | null {
  // Edge case: no quotes available
  if (quotes.length === 0) {
    return null;
  }

  // Edge case: only one quote
  if (quotes.length === 1) {
    return quotes[0];
  }

  // Generate deterministic hash from date
  const hash = useAdvancedHash
    ? advancedHash(dateString)
    : hashDateString(dateString);

  // Map hash to a valid index
  const index = hash % quotes.length;

  return quotes[index];
}

/**
 * Select a truly random quote (for manual refresh feature)
 * Not deterministic - uses Math.random()
 *
 * @param quotes - Array of all available quotes
 * @param excludeId - Optional quote ID to exclude (e.g., current quote)
 * @returns Randomly selected quote, or null if no quotes available
 */
export function selectRandomQuote(
  quotes: Quote[],
  excludeId?: string
): Quote | null {
  if (quotes.length === 0) {
    return null;
  }

  // Filter out excluded quote if specified
  let availableQuotes = quotes;
  if (excludeId) {
    availableQuotes = quotes.filter((q) => q.id !== excludeId);
  }

  // If no quotes left after filtering, return null
  if (availableQuotes.length === 0) {
    return null;
  }

  // Select random index
  const randomIndex = Math.floor(Math.random() * availableQuotes.length);
  return availableQuotes[randomIndex];
}

/**
 * Check if a new daily quote should be selected
 *
 * @param currentDate - Current date string (YYYY-MM-DD)
 * @param lastSelectedDate - Last date when quote was selected
 * @param dailyQuoteId - Current daily quote ID
 * @param quotes - Array of all quotes
 * @returns True if a new quote should be selected
 */
export function shouldSelectNewQuote(
  currentDate: string,
  lastSelectedDate: string | null,
  dailyQuoteId: string | null,
  quotes: Quote[]
): boolean {
  // No quotes available
  if (quotes.length === 0) {
    return false;
  }

  // No daily quote set yet
  if (!dailyQuoteId) {
    return true;
  }

  // Date has changed since last selection
  if (lastSelectedDate !== currentDate) {
    return true;
  }

  // Daily quote was deleted
  const quoteExists = quotes.some((q) => q.id === dailyQuoteId);
  if (!quoteExists) {
    return true;
  }

  // No need to select new quote
  return false;
}

/**
 * Get statistics about quote selection distribution
 * Useful for debugging/testing the hash algorithm
 *
 * @param quotes - Array of quotes
 * @param startDate - Start date for simulation
 * @param days - Number of days to simulate
 * @returns Map of quote ID to selection count
 */
export function analyzeQuoteDistribution(
  quotes: Quote[],
  startDate: Date,
  days: number
): Map<string, number> {
  const distribution = new Map<string, number>();

  // Initialize counts
  quotes.forEach((quote) => {
    distribution.set(quote.id, 0);
  });

  // Simulate selection for each day
  for (let i = 0; i < days; i++) {
    const date = new Date(startDate);
    date.setDate(date.getDate() + i);

    const dateString = date.toISOString().split('T')[0];
    const selectedQuote = selectDailyQuote(quotes, dateString);

    if (selectedQuote) {
      const currentCount = distribution.get(selectedQuote.id) || 0;
      distribution.set(selectedQuote.id, currentCount + 1);
    }
  }

  return distribution;
}
